// The Jobs pages' actions: ONE copy, used by the desktop Jobs pages (pages/JobsPage.js,
// pages/JobsDetailsPage.js) and the phone (mobile/screens/JobDetailScreen.jsx). An action that
// talks to the person takes a `ui` (features/desktopUi.js on the desktop, mobile/ui/phoneUi.js on a
// phone): pin(message), alert(message).
import config from "../../config.js";
import { apiErrorMessage, detailToString } from "../../utils/apiError";

const API_BASE = config.API_BASE_URL;
const noop = () => {};

/** PATCH /jobs/{id} with the given fields. Throws Error(the API's sentence) when refused. */
export async function patchJob(jobId, patch) {
  const res = await fetch(`${API_BASE}/jobs/${jobId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await apiErrorMessage(res));
}

/** What is left to ship of a job part. */
export function remainingToShip(p) {
  return Math.max(0, (Number(p.quantity_ordered) || 0) - (Number(p.quantity_shipped) || 0));
}

/**
 * The Pre-ship request for the quantities typed per part_id: {items} or {error}. An assembly
 * component ships with its assembly, so it is never sent. Nothing typed, or more than is left, is
 * refused before any PIN.
 */
export function preShipItems(parts, qtys) {
  const items = (parts || [])
    .filter((p) => !p.component_of_assembly)
    .map((p) => ({ part_id: p.part_id, quantity: parseInt(qtys[p.part_id], 10) || 0 }))
    .filter((it) => it.quantity > 0);
  if (items.length === 0) return { error: "Enter a quantity for at least one part." };
  for (const it of items) {
    const p = parts.find((x) => x.part_id === it.part_id);
    if (p && it.quantity > remainingToShip(p)) return { error: `Qty for ${p.product_id} exceeds remaining (${remainingToShip(p)}).` };
  }
  return { items };
}

/**
 * Pre-ship: carve the items off now (POST /jobs/{id}/pre_ship, PIN). They drop into the shipping
 * queue. Resolves {done, total} (after saying so), {error} (the API's sentence; the screen shows
 * it next to the quantities) or {cancelled} (no PIN).
 */
export async function preShip({ jobId, items, ui, setBusy = noop }) {
  const pin = await ui.pin("Enter PIN to pre-ship:");
  if (!pin) return { cancelled: true };
  setBusy(true);
  try {
    const res = await fetch(`${API_BASE}/jobs/${jobId}/pre_ship`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items, pin }),
    });
    const data = await res.json();
    if (!res.ok) return { error: detailToString(data.detail) || `Pre-ship failed (${res.status})` };
    const total = items.reduce((s, i) => s + i.quantity, 0);
    await ui.alert(`Pre-shipped ${total} pc(s) — now in the Shipping queue.`);
    return { done: true, total };
  } catch (e) {
    return { error: e.message };
  } finally {
    setBusy(false);
  }
}

/**
 * Attach a file to the job (POST /jobs/{id}/documents, PIN): a PO, a drawing, a photo. A file and
 * a name are required. Resolves {reload: true} once saved (after saying so).
 */
export async function uploadJobDocument({ jobId, file, displayName, description = "", ui, setBusy = noop }) {
  if (!file) {
    await ui.alert("Please select a file.");
    return { reload: false };
  }
  if (!String(displayName || "").trim()) {
    await ui.alert("Please enter a file name.");
    return { reload: false };
  }
  const pin = await ui.pin("Enter PIN to upload document:");
  if (!pin) return { reload: false };

  setBusy(true);
  const formData = new FormData();
  formData.append("file", file);
  formData.append("display_name", displayName);
  formData.append("description", description);
  formData.append("pin", pin);
  try {
    const res = await fetch(`${API_BASE}/jobs/${jobId}/documents`, { method: "POST", body: formData });
    if (!res.ok) throw new Error(await apiErrorMessage(res));
    await ui.alert("File uploaded successfully!");
    return { reload: true };
  } catch (err) {
    await ui.alert("Upload failed: " + err.message);
    return { reload: false };
  } finally {
    setBusy(false);
  }
}
